import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import { 
  UserPlusIcon, 
  UserMinusIcon, 
  EnvelopeIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ClockIcon,
  CreditCardIcon,
  LockClosedIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import FixPartnerAccessButton from '../../../components/FixPartnerAccessButton';

/**
 * Partner Settings Component
 * 👥 Manages partner relationships and privacy settings
 */
const PartnerSettings = () => {
  const { 
    user, 
    generatePartnerInvite, 
    acceptPartnerInvite, 
    updatePrivacySettings, 
    createPartnerProfile,
    updatePartnerProfile,
    removePartner,
    getPartnerInvitations,
    rejectPartnerInvite,
    refreshUserData
  } = useAuthContext();
  
  const [inviteToken, setInviteToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [partnerStatus, setPartnerStatus] = useState('none'); // none, connected, created
  const [invitations, setInvitations] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showSharedAccounts, setShowSharedAccounts] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(null);
  const [privacySettings, setPrivacySettings] = useState({
    shareIncome: user?.privacySettings?.shareIncome ?? true,
    shareExpenses: user?.privacySettings?.shareExpenses ?? true,
    shareSavings: user?.privacySettings?.shareSavings ?? true,
    shareDebt: user?.privacySettings?.shareDebt ?? true
  });
  
  // New partner form state
  const [partnerForm, setPartnerForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: ''
  });

  // Check partner status on component mount
  useEffect(() => {
    checkPartnerStatus();
    fetchInvitations();
  }, [user]);

  // Calculate time remaining for invitations
  useEffect(() => {
    const calculateTimeRemaining = () => {
      const remaining = {};
      invitations.forEach(invitation => {
        const expiresAt = new Date(invitation.expiresAt);
        const now = new Date();
        const diffMs = expiresAt - now;
        
        if (diffMs <= 0) {
          remaining[invitation._id] = 'Expired';
        } else if (diffMs < 3600000) { // less than an hour
          const minutes = Math.floor(diffMs / 60000);
          remaining[invitation._id] = `${minutes} minute${minutes !== 1 ? 's' : ''} left`;
        } else if (diffMs < 86400000) { // less than a day
          const hours = Math.floor(diffMs / 3600000);
          remaining[invitation._id] = `${hours} hour${hours !== 1 ? 's' : ''} left`;
        } else { // more than a day
          const days = Math.floor(diffMs / 86400000);
          remaining[invitation._id] = `${days} day${days !== 1 ? 's' : ''} left`;
        }
      });
      
      setTimeRemaining(remaining);
    };
    
    if (invitations.length > 0) {
      calculateTimeRemaining();
      const interval = setInterval(calculateTimeRemaining, 60000);
      return () => clearInterval(interval);
    }
  }, [invitations]);

  // Check if user has a partner and what type
  const checkPartnerStatus = () => {
    if (!user) return;
    
    if (user.partnerId) {
      setPartnerStatus('connected');
    } else if (user.createdPartner && user.createdPartner.firstName) {
      setPartnerStatus('created');
      // Pre-fill form with existing data for editing
      if (user.createdPartner) {
        setPartnerForm({
          firstName: user.createdPartner.firstName || '',
          lastName: user.createdPartner.lastName || '',
          email: user.createdPartner.email || '',
          phoneNumber: user.createdPartner.phoneNumber || '',
          dateOfBirth: user.createdPartner.dateOfBirth ? new Date(user.createdPartner.dateOfBirth).toISOString().split('T')[0] : ''
        });
      }
    } else {
      setPartnerStatus('none');
    }
  };

  // Fetch partner invitations
  const fetchInvitations = async () => {
    try {
      const response = await getPartnerInvitations();
      console.log('Fetched invitations in PartnerSettings:', response);
      
      // Check if invitations exist in the response
      if (!response.invitations || !Array.isArray(response.invitations)) {
        console.error('No invitations array in API response:', response);
        setInvitations([]);
        return;
      }
      
      // Get current user info for comparison
      const currentUserId = user._id;
      const currentUserEmail = user.email ? user.email.toLowerCase() : null;
      
      // Process sent invitations only - since we only want to show those here
      const userSentInvitations = response.invitations.filter(inv => {
        const invSenderId = inv.sender;
        const invSenderEmail = inv.senderEmail ? inv.senderEmail.toLowerCase() : null;
        
        // Check if current user is the sender
        return (invSenderId && invSenderId === currentUserId) || 
               (currentUserEmail && invSenderEmail === currentUserEmail);
      });
      
      // Process sent invitations to ensure they have recipient email
      const processedInvitations = userSentInvitations.map(inv => {
        if (!inv.recipientEmail && inv.recipient) {
          // Try to get the email from the recipient object if available in response
          const recipientUser = response.users?.find(u => u._id === inv.recipient);
          if (recipientUser && recipientUser.email) {
            return { ...inv, recipientEmail: recipientUser.email };
          }
        }
        return inv;
      });
      
      setInvitations(processedInvitations);
    } catch (error) {
      console.error('Failed to fetch invitations', error);
    }
  };

  // Handle generating partner invitation
  const handleGenerateInvite = async (email) => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }
    try {
      setLoading(true);
      const response = await generatePartnerInvite(email);
      console.log('Partner invite response in PartnerSettings:', response);
      
      // Check if the response indicates success
      const isSuccess = response && 
                        (response.success || 
                         response.message?.toLowerCase().includes('success') ||
                         response.status === 'success' ||
                         response.status === 200);
      
      if (isSuccess) {
        toast.success('Partner invitation sent successfully!');
        document.getElementById('partnerEmail').value = '';
        
        // Refresh invitations list
        await fetchInvitations();
      } else {
        // If we have a specific error message, show it
        if (response && response.message) {
          toast.error(response.message);
        } else {
          toast.error('Failed to generate partner invitation');
        }
      }
    } catch (error) {
      // Check if the error response indicates the invitation was actually sent
      // Sometimes the rate limiter throws an error but the email is still sent
      if (error.response?.data?.success || 
          error.message?.includes('rate limit') ||
          error.message?.includes('trust proxy')) {
        toast.success('Partner invitation sent successfully!');
        document.getElementById('partnerEmail').value = '';
        
        // Refresh invitations list
        await fetchInvitations();
      } else {
        console.error('Partner invitation error:', error);
      toast.error(error.message || 'Failed to generate partner invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle accepting partner invitation
  const handleAcceptInvite = async (e) => {
    e.preventDefault();
    if (!inviteToken) {
      toast.error('Please enter an invitation token');
      return;
    }

    try {
      setLoading(true);
      await acceptPartnerInvite(inviteToken);
      toast.success('Partner invitation accepted successfully!');
      setInviteToken('');
      
      // Refresh user data to get updated partner info
      await refreshUserData();
      checkPartnerStatus();
    } catch (error) {
      toast.error(error.message || 'Failed to accept partner invitation');
    } finally {
      setLoading(false);
    }
  };

  // Handle creating/updating partner profile
  const handlePartnerFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Format date of birth if provided
      const formData = {...partnerForm};
      if (formData.dateOfBirth) {
        formData.dateOfBirth = new Date(formData.dateOfBirth).toISOString();
      }
      
      if (partnerStatus === 'created') {
        // Update existing partner
        await updatePartnerProfile(formData);
        toast.success('Partner profile updated successfully!');
      } else {
        // Create new partner
        await createPartnerProfile(formData);
        toast.success('Partner profile created successfully!');
      }
      
      // Refresh user data
      await refreshUserData();
      checkPartnerStatus();
      setShowCreateForm(false);
    } catch (error) {
      toast.error(error.message || 'Failed to save partner profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle partner form input changes
  const handlePartnerFormChange = (e) => {
    const { name, value } = e.target;
    setPartnerForm({
      ...partnerForm,
      [name]: value
    });
  };

  // Handle removing partner
  const handleRemovePartner = async () => {
    try {
      setLoading(true);
      await removePartner();
      toast.success('Partner removed successfully');
      setShowDisconnectConfirm(false);
      
      // Reset form and status
      setPartnerForm({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: ''
      });
      
      // Refresh user data
      await refreshUserData();
      checkPartnerStatus();
    } catch (error) {
      toast.error(error.message || 'Failed to remove partner');
    } finally {
      setLoading(false);
    }
  };

  // Handle revoking an invitation
  const handleRevokeInvitation = async (invitationId) => {
    try {
      setLoading(true);
      await rejectPartnerInvite(invitationId);
      toast.success('Invitation revoked successfully');
      setShowRevokeConfirm(null);
      
      // Refresh invitations
      await fetchInvitations();
    } catch (error) {
      toast.error('Failed to revoke invitation');
    } finally {
      setLoading(false);
    }
  };

  // Handle resending an invitation
  const handleResendInvitation = async (invitationId) => {
    try {
      setLoading(true);
      const invitation = invitations.find(inv => inv._id === invitationId);
      if (invitation) {
        await generatePartnerInvite(invitation.recipientEmail);
        toast.success('Invitation resent successfully');
      } else {
        toast.error('Invitation not found');
      }
      
      // Refresh invitations
      await fetchInvitations();
    } catch (error) {
      toast.error('Failed to resend invitation');
    } finally {
      setLoading(false);
    }
  };

  // Handle privacy settings change
  const handlePrivacyChange = async (setting) => {
    try {
      setLoading(true);
      
      // Update local state first for responsiveness
      const updatedSettings = {
        ...privacySettings,
        [setting]: !privacySettings[setting]
      };
      setPrivacySettings(updatedSettings);
      
      // Send to server
      await updatePrivacySettings(updatedSettings);
      toast.success('Privacy settings updated successfully');
    } catch (error) {
      // Revert state on error
      setPrivacySettings(privacySettings);
      toast.error(error.message || 'Failed to update privacy settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Debug Button - Temporary */}
      <div className="md:col-span-3">
        <FixPartnerAccessButton />
      </div>
      
      {/* Partner Connection Panel */}
      <div className="md:col-span-2 bg-white p-6 rounded-xl shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-indigo-700">Partner Connection</h3>
        
        {partnerStatus !== 'none' && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <span className="inline-flex mr-2 h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-indigo-500">
                  {user?.partner?.firstName?.[0]?.toUpperCase() || 'P'}
                </span>
                <div>
                  <div className="flex items-center">
                    <h4 className="font-medium mr-2">
                      {user?.partner?.firstName} {user?.partner?.lastName}
                    </h4>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${partnerStatus === 'connected' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                      {partnerStatus === 'connected' ? (
                        <><CheckCircleIcon className="h-3 w-3 mr-1" />Accepted</>
                      ) : (
                        'Created Profile'
                      )}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{user?.partner?.email || 'No email provided'}</p>
                </div>
              </div>
              
              <div className="flex space-x-2">
                <button 
                  onClick={() => setShowSharedAccounts(true)}
                  className="px-3 py-1 text-xs bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200 flex items-center"
                >
                  <CreditCardIcon className="h-3 w-3 mr-1" />
                  Shared Accounts
                </button>
                <button 
                  onClick={() => setShowDisconnectConfirm(true)}
                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200 flex items-center"
                >
                  <UserMinusIcon className="h-3 w-3 mr-1" />
                  Disconnect
                </button>
              </div>
          </div>
            
            {partnerStatus === 'created' && (
            <button
                onClick={() => setShowCreateForm(true)}
                className="mt-2 w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
            >
                Edit Partner Profile
            </button>
            )}
          </div>
        )}
        
        {partnerStatus === 'none' && (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Connect with your partner to share financial information and manage your household together.
            </p>
            
            <div className="mb-6 space-y-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Send an Invitation</h4>
                <div className="flex">
                  <input
                    type="email"
                    id="partnerEmail"
                    className="flex-grow rounded-l-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Enter partner's email"
                  />
                  <button
                    onClick={() => handleGenerateInvite(document.getElementById('partnerEmail').value)}
                    disabled={loading}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-r-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <EnvelopeIcon className="h-5 w-5 mr-1" />
                    Send Invitation
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h4 className="font-medium text-gray-700 mb-2">Create a Partner Profile</h4>
                <p className="text-sm text-gray-500 mb-2">
                  If your partner doesn't have an account, you can create a profile for them.
                </p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
                >
                  <UserPlusIcon className="h-5 w-5 inline mr-1" />
                  Create Partner Profile
                </button>
              </div>

              <form onSubmit={handleAcceptInvite} className="border-t border-gray-200 pt-6">
                <h4 className="font-medium text-gray-700 mb-2">Have an invitation code?</h4>
                <div className="flex space-x-2">
                <input
                  type="text"
                  id="inviteToken"
                  value={inviteToken}
                  onChange={(e) => setInviteToken(e.target.value)}
                    className="flex-grow rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Enter invitation token"
                />
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    Accept
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-700 mb-2">Pending Invitations</h4>
            <div className="space-y-3">
              {invitations.map((invitation) => {
                const isExpired = timeRemaining[invitation._id] === 'Expired';
                return (
                  <div key={invitation._id} className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <span className="text-sm font-medium">
                          {invitation.recipientEmail || 'Partner'}
                        </span>
                        <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${isExpired ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                          {isExpired ? 'Expired' : 'Pending'}
                        </span>
                      </div>
                      <span className={`text-xs ${isExpired ? 'text-red-500' : 'text-amber-500'} flex items-center`}>
                        <ClockIcon className="h-3 w-3 mr-1" />
                        {timeRemaining[invitation._id] || 'Calculating...'}
                      </span>
                    </div>
                    
                    <div className="flex justify-end mt-2 space-x-2">
                      <button
                        onClick={() => setShowRevokeConfirm(invitation._id)}
                        className="px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                      >
                        Cancel
                      </button>
                      {isExpired && (
                        <button
                          onClick={() => handleResendInvitation(invitation._id)}
                          className="px-2 py-1 text-xs text-indigo-600 border border-indigo-200 rounded hover:bg-indigo-50"
                        >
                          Resend
                        </button>
                      )}
                    </div>
                    
                    {/* Revoke Confirmation */}
                    {showRevokeConfirm === invitation._id && (
                      <div className="mt-2 p-2 border border-red-100 rounded bg-red-50">
                        <p className="text-xs text-red-700 mb-2">
                          Are you sure you want to cancel this invitation?
                        </p>
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => setShowRevokeConfirm(null)}
                            className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300"
                          >
                            Keep It
                          </button>
                          <button
                            onClick={() => handleRevokeInvitation(invitation._id)}
                            className="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                          >
                            Yes, Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Partner Creation/Edit Form */}
        {showCreateForm && (
          <div className="mt-6 p-4 border border-gray-200 rounded-md bg-white">
            <h4 className="font-medium text-gray-700 mb-4">
              {partnerStatus === 'created' ? 'Edit Partner Profile' : 'Create Partner Profile'}
            </h4>
            
            <form onSubmit={handlePartnerFormSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={partnerForm.firstName}
                    onChange={handlePartnerFormChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={partnerForm.lastName}
                    onChange={handlePartnerFormChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={partnerForm.email}
                    onChange={handlePartnerFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={partnerForm.phoneNumber}
                    onChange={handlePartnerFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={partnerForm.dateOfBirth}
                    onChange={handlePartnerFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              <button
                type="submit"
                disabled={loading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                  {partnerStatus === 'created' ? 'Update Partner' : 'Create Partner'}
              </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Privacy Settings */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-indigo-700">Privacy Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Share Income</label>
            <button
              onClick={() => handlePrivacyChange('shareIncome')}
              className={`${
                privacySettings.shareIncome ? 'bg-indigo-600' : 'bg-gray-300'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
            >
              <span
                className={`${
                  privacySettings.shareIncome ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Share Expenses</label>
            <button
              onClick={() => handlePrivacyChange('shareExpenses')}
              className={`${
                privacySettings.shareExpenses ? 'bg-indigo-600' : 'bg-gray-300'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
            >
              <span
                className={`${
                  privacySettings.shareExpenses ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Share Savings</label>
            <button
              onClick={() => handlePrivacyChange('shareSavings')}
              className={`${
                privacySettings.shareSavings ? 'bg-indigo-600' : 'bg-gray-300'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
            >
              <span
                className={`${
                  privacySettings.shareSavings ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Share Debt</label>
            <button
              onClick={() => handlePrivacyChange('shareDebt')}
              className={`${
                privacySettings.shareDebt ? 'bg-indigo-600' : 'bg-gray-300'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
            >
              <span
                className={`${
                  privacySettings.shareDebt ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </button>
          </div>
        </div>
        
        {/* Shared Account Settings Button */}
        {(partnerStatus === 'connected' || partnerStatus === 'created') && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={() => setShowSharedAccounts(true)}
              className="w-full py-2 px-4 border border-indigo-300 text-indigo-700 rounded-md hover:bg-indigo-50 flex items-center justify-center"
            >
              <CreditCardIcon className="h-4 w-4 mr-2" />
              Manage Shared Accounts
            </button>
          </div>
        )}
      </div>
      
      {/* Disconnect Confirmation Modal */}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-start mb-4">
              <div className="bg-red-100 rounded-full p-2 mr-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Disconnect Partner</h2>
                <p className="text-gray-600 mt-1">
                  Are you sure you want to disconnect from {user?.partner?.firstName} {user?.partner?.lastName}?
                </p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-100 rounded-md p-3 mb-4">
              <p className="text-sm text-red-800">
                This will remove all partner-related data and connections. This action cannot be undone.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleRemovePartner}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Disconnect Partner'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Shared Accounts Modal */}
      {showSharedAccounts && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-start mb-4">
              <div className="bg-indigo-100 rounded-full p-2 mr-3">
                <LockClosedIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Shared Accounts</h2>
                <p className="text-gray-600 mt-1">
                  Manage what accounts are shared with {user?.partner?.firstName} {user?.partner?.lastName}
                </p>
              </div>
            </div>
            
            <div className="mb-4 max-h-64 overflow-y-auto">
              <p className="text-sm text-gray-600 mb-2">Select accounts to share with your partner:</p>
              
              {/* This would be populated with actual account data from an API call */}
              <div className="space-y-2">
                <div className="flex items-center justify-between border border-gray-200 rounded p-2">
                  <span className="text-sm font-medium text-gray-700">Checking Account</span>
                  <label className="inline-flex items-center">
                    <input type="checkbox" className="rounded text-indigo-600" />
                    <span className="ml-2 text-xs text-gray-700">Share</span>
                  </label>
                </div>
                <div className="flex items-center justify-between border border-gray-200 rounded p-2">
                  <span className="text-sm font-medium text-gray-700">Savings Account</span>
                  <label className="inline-flex items-center">
                    <input type="checkbox" className="rounded text-indigo-600" />
                    <span className="ml-2 text-xs text-gray-700">Share</span>
                  </label>
                </div>
                <div className="flex items-center justify-between border border-gray-200 rounded p-2">
                  <span className="text-sm font-medium text-gray-700">Credit Card</span>
                  <label className="inline-flex items-center">
                    <input type="checkbox" className="rounded text-indigo-600" />
                    <span className="ml-2 text-xs text-gray-700">Share</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowSharedAccounts(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // This would save the shared account settings
                  toast.success('Shared account settings updated');
                  setShowSharedAccounts(false);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerSettings;